import type { CompanyGroup } from './types';

/**
 * Institutions-then-Partners grouping on project/festival lists (owner
 * 2026-10-18). The server decides the group and the order (backend
 * `lib/company-group.ts`); this file only words the sub-header and finds the
 * boundaries. It never re-sorts rows.
 */

/** "Institutions", "Partners": the type name in the plural. */
export function companyGroupLabel(name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') return 'Other';
  if (/s$/i.test(trimmed)) return trimmed;
  return /[^aeiou]y$/i.test(trimmed) ? `${trimmed.slice(0, -1)}ies` : `${trimmed}s`;
}

export function companyGroupKey(group: CompanyGroup | null | undefined): string {
  return group ? `g:${group.id}` : 'g:none';
}

/**
 * Splits rows into consecutive runs by group, in the order received. A run is
 * started whenever the group changes; nothing is moved.
 */
export function splitByCompanyGroup<T>(
  rows: readonly T[],
  groupOf: (row: T) => CompanyGroup | null | undefined,
): { key: string; label: string; rows: T[] }[] {
  const runs: { key: string; label: string; rows: T[] }[] = [];
  for (const row of rows) {
    const group = groupOf(row);
    const key = companyGroupKey(group);
    const last = runs[runs.length - 1];
    if (last && last.key === key) last.rows.push(row);
    else runs.push({ key, label: group ? companyGroupLabel(group.name) : 'Other', rows: [row] });
  }
  return runs;
}
